import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getHealthScore } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { UserBadge } from './DocumentPreview';
import { Activity, ArrowRight, TrendingDown, TrendingUp } from 'lucide-react';

const statusColors = {
    good: { bg: '#16A34A', light: '#DCFCE7', text: '#166534', label: 'Excellent' },
    warn: { bg: '#EA580C', light: '#FED7AA', text: '#9A3412', label: 'À surveiller' },
    bad: { bg: '#DC2626', light: '#FEE2E2', text: '#991B1B', label: 'Critique' },
};

const RADIUS = 70;
const STROKE = 10;
const CIRC = 2 * Math.PI * RADIUS;

const CircularGauge = ({ score, status, size = 180 }) => {
    const offset = CIRC - (score / 100) * CIRC;
    const color = statusColors[status]?.bg || '#52525B';
    return (
        <svg width={size} height={size} viewBox="0 0 180 180" className="transform -rotate-90">
            <circle cx="90" cy="90" r={RADIUS} fill="none" stroke="#F4F4F5" strokeWidth={STROKE} />
            <circle
                cx="90"
                cy="90"
                r={RADIUS}
                fill="none"
                stroke={color}
                strokeWidth={STROKE}
                strokeDasharray={CIRC}
                strokeDashoffset={offset}
                strokeLinecap="round"
                style={{ transition: 'stroke-dashoffset 800ms ease-out, stroke 400ms' }}
            />
        </svg>
    );
};

const SmallGauge = ({ score, status }) => {
    const size = 56;
    const r = 22;
    const c = 2 * Math.PI * r;
    const offset = c - (score / 100) * c;
    const color = statusColors[status]?.bg || '#52525B';
    return (
        <div className="relative" style={{ width: size, height: size }}>
            <svg width={size} height={size} viewBox="0 0 56 56" className="transform -rotate-90">
                <circle cx="28" cy="28" r={r} fill="none" stroke="#F4F4F5" strokeWidth="5" />
                <circle cx="28" cy="28" r={r} fill="none" stroke={color} strokeWidth="5" strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center text-xs font-bold" style={{ color }}>
                {score}
            </div>
        </div>
    );
};

export const HealthScoreCard = () => {
    const { user } = useAuth();
    const location = useLocation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const isAdminOrManager = user?.role === 'admin' || user?.role === 'manager';

    useEffect(() => {
        const load = async () => {
            try {
                const res = await getHealthScore();
                setData(res.data);
            } catch {
                setData(null);
            } finally {
                setLoading(false);
            }
        };
        load();
        const i = setInterval(load, 120000);
        const onFocus = () => load();
        window.addEventListener('focus', onFocus);
        return () => {
            clearInterval(i);
            window.removeEventListener('focus', onFocus);
        };
    }, [location.key]);

    if (loading) {
        return (
            <div className="bg-white border border-[#E4E4E7] rounded-sm p-6 flex items-center justify-center h-48">
                <div className="w-6 h-6 border-4 border-[#E85A24] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!data?.global) return null;

    const { global, per_commercial = [] } = data;
    const palette = statusColors[global.status];

    return (
        <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden" data-testid="health-score-card">
            <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-[#52525B]">
                            <Activity className="w-4 h-4" /> Santé du portefeuille
                        </div>
                        <h3 className="text-lg font-bold text-[#0A0A0A] mt-1">
                            {isAdminOrManager ? 'Vue globale' : 'Mon portefeuille'}
                        </h3>
                    </div>
                    <span
                        className="px-3 py-1 text-xs font-bold uppercase rounded-full"
                        style={{ backgroundColor: palette.light, color: palette.text }}
                    >
                        {palette.label}
                    </span>
                </div>

                <div className="flex items-center gap-6">
                    <div className="relative shrink-0">
                        <CircularGauge score={global.score} status={global.status} />
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-4xl font-black" style={{ color: palette.bg }} data-testid="health-score-value">
                                {global.score}
                            </span>
                            <span className="text-xs text-[#52525B] font-medium">/ 100</span>
                        </div>
                    </div>

                    <div className="flex-1 space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                            <span className="text-[#52525B]">Dossiers actifs</span>
                            <span className="font-semibold text-[#0A0A0A]">{global.total_active}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-[#52525B]">En retard</span>
                            <span className="font-bold" style={{ color: palette.bg }}>
                                {global.overdue_total}
                            </span>
                        </div>
                        <div className="pt-2 border-t border-[#E4E4E7] space-y-1 text-xs text-[#52525B]">
                            <div className="flex justify-between"><span>RDV sans devis</span><span>{global.breakdown.rdv_sans_devis}</span></div>
                            <div className="flex justify-between"><span>Devis sans retour</span><span>{global.breakdown.devis_sans_retour}</span></div>
                            <div className="flex justify-between"><span>Relances</span><span>{global.breakdown.relances_en_retard}</span></div>
                            <div className="flex justify-between"><span>Chantiers</span><span>{global.breakdown.chantiers_a_receptionner}</span></div>
                            <div className="flex justify-between"><span>Silencieux</span><span>{global.breakdown.clients_silencieux}</span></div>
                        </div>
                    </div>
                </div>

                <Link
                    to="/en-retard"
                    className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#E85A24] hover:underline"
                    data-testid="health-score-link"
                >
                    Voir les items en retard <ArrowRight className="w-4 h-4" />
                </Link>
            </div>

            {isAdminOrManager && per_commercial.length > 0 && (
                <div className="border-t border-[#E4E4E7] bg-[#FAFAFA]">
                    <div className="px-6 py-3 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-[#16A34A]" />
                        <h4 className="text-xs font-bold uppercase text-[#52525B] tracking-wide">Classement par commercial</h4>
                    </div>
                    <div className="divide-y divide-[#E4E4E7]">
                        {per_commercial.slice(0, 6).map((c, idx) => (
                            <div key={c.commercial_id} className="px-6 py-3 flex items-center gap-4" data-testid={`health-rank-${idx}`}>
                                <span className="text-xs font-bold w-6 text-[#52525B]">#{idx + 1}</span>
                                <SmallGauge score={c.score} status={c.status} />
                                <div className="flex-1 min-w-0">
                                    <UserBadge name={c.commercial_nom} id={c.commercial_id} />
                                    <p className="text-xs text-[#52525B] mt-1">
                                        {c.total_active} dossiers · <span className="font-semibold" style={{ color: statusColors[c.status]?.bg }}>{c.overdue_total} en retard</span>
                                    </p>
                                </div>
                                {idx === per_commercial.length - 1 && c.status === 'bad' && (
                                    <TrendingDown className="w-5 h-5 text-[#DC2626]" />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
